import React, { useState, useMemo, useCallback, useRef, useEffect, KeyboardEvent } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setOpenAmberCopilot,
  setCopilotInitialMessage,
} from "@Pages/PropertyPage/components/ZendeskChat/ChatBotReducer";
import { AppState } from "@Store/index";
import Button from "@Components/Button";
import SmartFilterLogo from "@Pages/Search/components/FilterSection/components/SmartFilters/icons/SmartFilterLogo";
import { getRandomQuestions } from "@Pages/PropertyPage/components/SmartQuestions/smartQuestionsData";
import {
  trackSmartQuestionChipClick,
  trackSmartQuestionSubmitted,
} from "@Pages/PropertyPage/components/SmartQuestions/analytics";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import styles from "../faq.module.scss";

interface SmartSearchInputProps {
  searchTerm: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  excludeQuestions?: string[];
  questions?: string[];
  onClearSearch?: () => void;
}

const ChipArrowIcon = () => (
  <svg
    className={styles.chipIcon}
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M2.4 5.1C2.4 4.88 2.22 4.7 2 4.7C1.78 4.7 1.6 4.88 1.6 5.1V7.5C1.6 8.82 2.67 9.9 4 9.9H13.03L10.12 12.82C9.96 12.97 9.96 13.23 10.12 13.38C10.27 13.54 10.53 13.54 10.68 13.38L14.28 9.78C14.36 9.71 14.4 9.6 14.4 9.5C14.4 9.39 14.36 9.29 14.28 9.22L10.68 5.62C10.53 5.46 10.27 5.46 10.12 5.62C9.96 5.77 9.96 6.03 10.12 6.18L13.03 9.1H4C3.12 9.1 2.4 8.38 2.4 7.5V5.1Z"
      fill="#111928"
    />
  </svg>
);

const SendIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M15.83 8.42C16.05 8.19 16.05 7.81 15.83 7.58L9.55 1.06C9.33 0.83 8.96 0.83 8.74 1.06C8.52 1.29 8.52 1.67 8.74 1.9L14.05 7.41H0.57C0.26 7.41 0 7.67 0 8C0 8.33 0.26 8.59 0.57 8.59H14.05L8.74 14.1C8.52 14.33 8.52 14.71 8.74 14.94C8.96 15.17 9.33 15.17 9.55 14.94L15.83 8.42Z"
      fill="white"
    />
  </svg>
);

const SmartSearchInput: React.FC<SmartSearchInputProps> = ({
  searchTerm,
  onSearchChange,
  excludeQuestions = [],
  questions,
  onClearSearch,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslationHook();
  const isChatbotOpen = useSelector(({ chatBotReducer }: AppState) => chatBotReducer.isChatbotOpen);
  const questionKeys = useMemo(
    () => questions ?? getRandomQuestions(excludeQuestions),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [questions],
  );

  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [mask, setMask] = useState<"right" | "left" | "both" | "none">("right");
  const [selectedChipKey, setSelectedChipKey] = useState<string | null>(null);

  const getMaskState = (el: HTMLDivElement): "right" | "left" | "both" | "none" => {
    const canScrollRight = el.scrollWidth > el.clientWidth + el.scrollLeft + 1;
    const canScrollLeft = el.scrollLeft > 1;
    if (canScrollLeft && canScrollRight) return "both";
    if (canScrollLeft) return "left";
    if (canScrollRight) return "right";
    return "none";
  };

  const updateMasks = useCallback(() => {
    if (wrapperRef.current) setMask(getMaskState(wrapperRef.current));
  }, []);

  useEffect(() => {
    updateMasks();
    if (typeof window !== "undefined") {
      window.addEventListener("resize", updateMasks);
      return () => window.removeEventListener("resize", updateMasks);
    }
    return undefined;
  }, [updateMasks, questionKeys]);

  const handleWrapperScroll = useCallback(() => {
    updateMasks();
  }, [updateMasks]);

  const getMaskClass = (maskVal: "right" | "left" | "both" | "none") => {
    if (maskVal === "both") return styles.smartChipsMaskBoth;
    if (maskVal === "left") return styles.smartChipsMaskLeft;
    if (maskVal === "right") return styles.smartChipsMaskRight;
    return "";
  };

  const handleSubmit = useCallback(() => {
    const trimmedTerm = searchTerm.trim();
    if (trimmedTerm) {
      trackSmartQuestionSubmitted({
        section: "faq_smart_search",
        questionText: trimmedTerm,
        isChatbotOpen,
      });
      dispatch(setCopilotInitialMessage(trimmedTerm));
      if (!isChatbotOpen) {
        dispatch(setOpenAmberCopilot(true));
      }
      setSelectedChipKey(null);
      onClearSearch?.();
    }
  }, [dispatch, searchTerm, isChatbotOpen, onClearSearch]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  const handleChipClick = useCallback(
    (key: string, translatedText: string) => {
      setSelectedChipKey(key);
      trackSmartQuestionChipClick({
        section: "faq_smart_search",
        questionKey: key,
        isChatbotOpen,
      });
      // Directly open Copilot with the question
      dispatch(setCopilotInitialMessage(translatedText));
      if (!isChatbotOpen) {
        dispatch(setOpenAmberCopilot(true));
      }
    },
    [dispatch, isChatbotOpen],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSelectedChipKey(null);
      onSearchChange(e);
    },
    [onSearchChange],
  );

  const firstRow = questionKeys.slice(0, 3);
  const secondRow = questionKeys.slice(3, 6);

  return (
    <div className={styles.smartSearch}>
      {/* Input */}
      <div className={styles.smartSearchInputOuter}>
        <div className={styles.smartSearchInputInner}>
          <SmartFilterLogo id="faqSmartIcon" />
          <input
            ref={inputRef}
            data-testid="FAQ-smart-search-bar"
            className={styles.smartSearchField}
            value={searchTerm}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={t("propertypage.smartquestions.faqplaceholder")}
          />
          <Button
            variant="none"
            className={`${styles.smartSearchSend} ${
              searchTerm.trim() ? styles.smartSearchSendActive : ""
            }`}
            onClick={handleSubmit}
            dataTestId="faq-smart-search-send"
          >
            <SendIcon />
          </Button>
        </div>
      </div>

      {/* Chips */}
      <div
        className={`${styles.smartChipsWrapper} ${getMaskClass(mask)}`}
        ref={wrapperRef}
        onScroll={handleWrapperScroll}
      >
        <div className={styles.smartChipsRow}>
          {firstRow.map((key) => {
            const text = t(key);
            return (
              <Button
                key={key}
                variant="none"
                className={`${styles.smartChip} ${
                  selectedChipKey === key ? styles.smartChipSelected : ""
                }`}
                onClick={() => handleChipClick(key, text)}
                dataTestId={`faq-smart-chip-${key}`}
              >
                <ChipArrowIcon />
                <span>{text}</span>
              </Button>
            );
          })}
        </div>
        <div className={styles.smartChipsRow}>
          {secondRow.map((key) => {
            const text = t(key);
            return (
              <Button
                key={key}
                variant="none"
                className={`${styles.smartChip} ${
                  selectedChipKey === key ? styles.smartChipSelected : ""
                }`}
                onClick={() => handleChipClick(key, text)}
                dataTestId={`faq-smart-chip-${key}`}
              >
                <ChipArrowIcon />
                <span>{text}</span>
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default wrapperHOC(SmartSearchInput, {
  componentName: "FAQs-SmartSearchInput",
  showForChina: false,
  showForWhiteLable: true,
});
